import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time failures so a single broken component cannot leave the
 * visitor staring at a blank page (spec 62: never show a blank screen).
 *
 * The message shown is generic on purpose. React error text routinely contains
 * component internals, and this boundary wraps pages that will later render
 * account data.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Phase 11 replaces this with the real telemetry sink.
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-hull-950 px-6">
        <div className="max-w-md rounded-card border border-hull-700 bg-hull-900 p-8 text-center">
          <h1 className="text-xl font-semibold text-white">Something went wrong</h1>
          <p className="mt-3 text-sm text-hull-300">
            The page failed to load. Reloading usually clears it. If it keeps
            happening, contact support and mention what you were doing.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 rounded-lg bg-surge-500 px-5 py-2.5 text-sm font-semibold text-hull-950 transition hover:bg-surge-400"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
